/* eslint-disable @angular-eslint/component-selector */
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

export interface StepperStepConfig {
  title: string;
  subtitle?: string;

  allowJumpToStep?: boolean;
  routerLink?: string;
  hideStep?: boolean;
}

export interface StepperConfig {
  mode?: 'vertical' | 'horizontal' | 'horizontal_navigation';

  activeStep?: number;
  /** Furthest step reached so far (e.g. when navigating back). Defaults to `activeStep`. */
  completedStep?: number;
  steps?: StepperStepConfig[];
}

@Component({
  selector: 'vs-stepper',
  templateUrl: './stepper.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class StepperComponent {
  constructor(
    private router: Router,
    private route: ActivatedRoute
  ) {}

  @Input() set config(config: StepperConfig) {
    this.mode = config?.mode ?? 'vertical';
    this.steps = config.steps || [];
    this.hasExplicitCompletedStep = config.completedStep !== undefined;
    this.selectedStep = config.activeStep || 1;
    this.completedStep = this.clampStep(config.completedStep ?? this.activeStep);
  }

  @Output() stepChanged = new EventEmitter<number>();

  mode = 'vertical';
  steps: StepperStepConfig[] = [];

  activeStep = 1;
  set selectedStep(step: number) {
    if (step === -1) this.activeStep = -1;
    else if (!step || step < 1) this.activeStep = 1;
    else if (step > this.steps.length) this.activeStep = this.steps.length;
    else this.activeStep = step;

    if (!this.hasExplicitCompletedStep) {
      this.completedStep = this.clampStep(this.activeStep);
    }

    this.stepChanged.emit(this.activeStep);
  }

  /** Furthest step reached, used to show already-completed steps even after navigating back. */
  completedStep = 1;
  private hasExplicitCompletedStep = false;

  private clampStep(step: number): number {
    const maxStep = Math.max(1, this.steps.length);
    if (!step || step < 1) return 1;
    if (step > maxStep) return maxStep;
    return step;
  }

  onClick(step: StepperStepConfig, index: number) {
    if (step.allowJumpToStep) this.selectedStep = index + 1;
    if (step.routerLink !== undefined) this.navigateTo(step.routerLink);
  }

  navigateTo(path: string) {
    this.router.navigate([`./${path}`], { relativeTo: this.route });
  }

  checkPreviousSteps(steps: StepperStepConfig[], index: number) {
    for (let i = 0; i < index; i++) {
      if (steps[i].hideStep !== true) {
        return false;
      }
    }
    return true;
  }
}
